import Folder, {FolderTypeEnum} from "../domain/Folder";
import {locationManager} from "../BootStrap";
import {LocationProperties} from "../util/LocationParser";
import {Dispatch} from "redux";
import {EmailActions} from "../actions/EmailActions";
import {AppState} from "../AppState";
import {
    GET_GREYLISTED_EMAILS_ASYNC,
    SELECT_FOLDER,
    SelectFolderAction,
    SET_NOTES_SEARCH_TEXT,
    SET_SEARCH_TEXT,
    SetNotesSearchTextAction,
    SetSearchTextAction
} from "../actions/FolderActionTypes";
import {GreylistFolderId, NotesFolderId, PhotosFolderId, ProtectedFilesFolderId, SearchFolderId} from '../reducers/FolderReducer';
import {performFetch, performRestCallDirectly} from '../util/HttpHelper';
import {GetGreylistedEmailsRequestDto} from '../services/messages/requests/GetGreylistedEmailsRequestDto';
import {GreylistedEmailResponseDto} from '../services/messages/responses/GreylistedEmailResponseDto';
import {CalendarState} from '../state/CalendarState';
import {CalendarLocation, ensureCalendarsLoaded} from './CalendarLocation';
import {EmailDataSourceResult} from "../domain/EmailDataSource";
import {FolderActions} from "../actions/FolderActions";
import {convertEmailHeader} from "../services/messages/EmailHeaderDto";
import NextFolderPageDto from "../services/messages/NextFolderPageDto";
import NextFolderPageResultDto from "../domain/NextFolderPageResultDto";
import {ContentLocation} from "./ContentLocation";
import {createAppProperties} from "./LocationUtil";
import {DB} from "../db/DbManager";
import {RxSubjects} from "../state/RxSubjectsState";
import {AppDispatch, AppStore} from "../AppStore";
import {AccountsActions} from "../redux/AccountsSlice";
import {NotesState} from "../state/NotesState";
import {NotesLocation} from "./NotesLocation";
import {ProtectedFilesService} from "../services/protected/ProtectedFilesService";
import {PreferenceActions} from "../redux/PreferencesSlice";
import {ensurePgpKeysLoaded} from "../util/PGPUtil";
import {AccountsState} from "../state/AccountState";
import {IDatasource} from "ag-grid-community/dist/types/src/interfaces/iDatasource";
import {IGetRowsParams} from "ag-grid-enterprise";

export interface FolderLocationProperties {
    folderId?: number;
    searchText?: string;
    notesSearchText?: string;
}

export const FolderLocationPropertyNames = createAppProperties<FolderLocationProperties>("folderId", "searchText", "notesSearchText");

async function displayAllCalendars(dispatch: Dispatch, calendarState: CalendarState) {
    await ensureCalendarsLoaded(dispatch, calendarState);
    CalendarLocation.setMonthWeekAndYear(calendarState.year, calendarState.month, calendarState.week);
}

async function displayAccounts(dispatch: Dispatch, accountsState: AccountsState) {

    ensurePgpKeysLoaded(dispatch, accountsState)
        .catch(e => console.error("Unable to load all PGP keys", e));

    const activeAccounts = await DB.getAllActiveGateKeeperAccounts();

    dispatch(AccountsActions.accountsLoaded(activeAccounts));
}

function handleError(error: Error, message: string) {
    console.error(message, error);// TODO: handle the errors better
}

function createSetSearchTextAction(searchText: string): SetSearchTextAction {
    return {type: SET_SEARCH_TEXT, searchText};
}

function createSetNotesSearchTextAction(notesSearchText: string): SetNotesSearchTextAction {
    return {type: SET_NOTES_SEARCH_TEXT, notesSearchText};
}

function createSelectFolderAction(folderId: number, virtualDataSource?: IDatasource): SelectFolderAction {
    return {type: SELECT_FOLDER, folderId, virtualDataSource};
}

async function displayGreylistedEmailsFolder(dispatch: AppDispatch, folderId: number) {
    // TODO: allow paging the greylisted emails
    const request: GetGreylistedEmailsRequestDto = {PageSize: 200, Offset: 0};

    dispatch({type: GET_GREYLISTED_EMAILS_ASYNC, request});

    try {
        const type = folderId === GreylistFolderId ? "GetGreylistedEmails" : "GetDLQEmails";
        const response = await performFetch(`/Mail3/${type}/${request.Offset}/${request.PageSize}`) as GreylistedEmailResponseDto;
        dispatch({type: GET_GREYLISTED_EMAILS_ASYNC, response});
    } catch (error) {
        dispatch({type: GET_GREYLISTED_EMAILS_ASYNC, error: error as Error});
    }
}

export function selectInboxFolderByDefault(store: AppStore) {
    const {folderState} = store.getState();
    if (!folderState.selectedFolderId) {
        const inboxFolderId = folderState.folderList.find(f => f.type === FolderTypeEnum.Inbox)?.folderId;
        if (inboxFolderId) {
            FolderLocation.selectFolderById(inboxFolderId);
        }
    }
}

let nextPageRequestId = 5000;

async function getNextVirtualFolderResultsPage(folderId: number, offset: number, pageSize: number, dispatch: Dispatch): Promise<EmailDataSourceResult> {
    try {
        nextPageRequestId++;

        const request: NextFolderPageDto = {
            RequestId: nextPageRequestId,
            FolderId: folderId,
            Offset: offset,
            PageSize: pageSize,
        };

        console.debug("Loading next folder page: ", request);
        const newResults = await performRestCallDirectly<NextFolderPageResultDto>("NextFolderPage", request);

        if (newResults.Emails) {
            const pageResult: EmailDataSourceResult = {
                emails: newResults.Emails.map(convertEmailHeader),
                lastEmail: newResults.Emails.length < pageSize,
                offset,
                pageSize,
            };
            dispatch(FolderActions.searchResultsLoaded());

            return pageResult;
        }

    } catch (e) {
        console.error("Failed to load the next set of rows", e);
        dispatch(FolderActions.performSearchAsync(undefined, undefined, new Error("A problem occurred while fetching the next set of emails")));
    }
    throw Error();
}

function displayDefaultNotesSectionAndPage(dispatch: AppDispatch, notesState: NotesState) {
    if (!notesState.selectedPage && notesState.sections && notesState.sections.length > 0) {
        NotesLocation.selectSectionByClientId(notesState.sections[0].ClientId || "");
    }
}

export class FolderLocation {
    static selectFolderById(folderId: number) {
        locationManager.updateWindowLocation("email", {folderId});
    }

    static selectFolder(folder: Folder) {
        locationManager.modifyWindowLocation("email", location => {
            location.folderId = -1000;
            delete location.itemId;
            delete location.itemIdStr;
            delete location.itemType;
        });
        locationManager.updateWindowLocation("email", {folderId: folder.folderId});
    }

    static searchForText(searchText: string) {
        if (searchText && searchText.length > 0) {
            locationManager.updateWindowLocation("email", {folderId: SearchFolderId, searchText});
        } else {
            locationManager.modifyWindowLocation("email", location => delete location.searchText);
        }
    }

    static searchForNotesText(notesSearchText: string) {
        if (notesSearchText && notesSearchText.length > 0) {
            locationManager.updateWindowLocation("email", {folderId: NotesFolderId, notesSearchText});
        } else {
            locationManager.modifyWindowLocation("email", location => delete location.notesSearchText);
        }
    }

    static handleLocationChange(dispatch: AppDispatch, getState: () => AppState, newLocation: LocationProperties, oldLocation: LocationProperties) {

        const newFolderId = newLocation.folderId || -999;

        if (newLocation.searchText !== oldLocation.searchText) {
            const newSearchText = newLocation.searchText ?? "";

            dispatch(createSetSearchTextAction(newSearchText));
            RxSubjects.searchText$.next(newSearchText);
        }
        if (newLocation.notesSearchText !== oldLocation.notesSearchText) {
            const newNotesSearchText = newLocation.notesSearchText ?? "";

            dispatch(createSetNotesSearchTextAction(newNotesSearchText));
            RxSubjects.notesSearchText$.next(newNotesSearchText);
        }
        if ((newLocation.folderId !== oldLocation.folderId && newFolderId) || (getState().folderState.selectedFolder === undefined && newFolderId)) {

            dispatch(createSelectFolderAction(newFolderId));

            const {folderState, calendarState, notesState, accountsState} = getState();
            const {selectedFolderId, selectedFolder} = folderState;

            if (!selectedFolderId || !selectedFolder) {
                return;
            }
            dispatch(EmailActions.showEmailHeaders([], false, true));

            const isVirtualSource = DB.isPrivateBrowserMode()
                && selectedFolder.type !== FolderTypeEnum.Calendar
                && selectedFolder.type !== FolderTypeEnum.ProtectedFiles;

            if (isVirtualSource) {
                const dataSource: IDatasource = {
                    rowCount: undefined,
                    getRows: (params: IGetRowsParams) => {
                        getNextVirtualFolderResultsPage(selectedFolderId, params.startRow, params.startRow - params.endRow, dispatch).then(
                            results => params.successCallback(results.emails),
                            () => params.failCallback(),
                        );
                    },
                };

                dispatch(createSelectFolderAction(selectedFolderId, dataSource));

            } else {
                switch (selectedFolder.type) {
                    case FolderTypeEnum.Notes:
                        displayDefaultNotesSectionAndPage(dispatch, notesState);
                        break;
                    case FolderTypeEnum.Outbox:
                        break;
                    // case FolderTypeEnum.Drafts:
                    //     break;
                    case FolderTypeEnum.Contacts:
                        break;
                    case FolderTypeEnum.Calendar:
                        ContentLocation.clearItemContent();
                        displayAllCalendars(dispatch, calendarState)
                            .catch(e => handleError(e, "Calendars"));
                        break;
                    case FolderTypeEnum.Accounts:
                        displayAccounts(dispatch, accountsState)
                            .catch(e => handleError(e, "Accounts"));
                        break;
                    case FolderTypeEnum.Greylist:
                        displayGreylistedEmailsFolder(dispatch, selectedFolderId)
                            .catch(e => handleError(e, "Greylisted Emails"));
                        break;
                    case FolderTypeEnum.ProtectedFiles:
                    case FolderTypeEnum.Attachments:
                    case FolderTypeEnum.Files:
                    case FolderTypeEnum.Photos:
                        if (selectedFolderId === ProtectedFilesFolderId) {
                            dispatch(PreferenceActions.changeFileFolder("protected"));
                            ProtectedFilesService.listProtectedFiles(dispatch)
                                .catch(e => handleError(e, "Protected Files"));
                        } else if (selectedFolderId === PhotosFolderId) {
                            dispatch(PreferenceActions.changeFileFolder("photos"));
                        } else {
                            dispatch(PreferenceActions.changeFileFolder("attachments"));
                        }
                        break;
                    default:
                        DB.getEmailHeadersInFolder(selectedFolderId)
                            .then(headers => {
                                // setTimeout(() => {
                                dispatch(EmailActions.showEmailHeaders(headers, true, false));
                                // }, 2000);
                            }, e => handleError(e, "Email Folder"));
                        break;
                }
            }
        }
    }
}
